import { Layers, Map as MapIcon, Sprout, Upload, FileSpreadsheet, FileDown, Search, Download, Trash2, Eraser, Wheat, Pencil, FileText, UserRound, History } from "lucide-react";
import { useRef, useMemo } from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { colorForKey } from "../lib/colors";

function LayerToggle({ icon: Icon, label, checked, onCheckedChange, testid, count }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-stone-200 bg-white px-3 py-2.5">
      <div className="flex items-center gap-2.5">
        <span className={`grid h-8 w-8 place-items-center rounded-md ${checked ? "bg-[#1a4731] text-white" : "bg-stone-100 text-stone-400"}`}>
          <Icon size={16} />
        </span>
        <div>
          <div className="text-sm font-medium text-stone-800">{label}</div>
          {count != null && <div className="text-xs text-stone-400">{count} parcelle(s)</div>}
        </div>
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testid} />
    </div>
  );
}

export default function Sidebar({
  parcels,
  showAerial, setShowAerial,
  showCadastre, setShowCadastre,
  showParcels, setShowParcels,
  showCultures, setShowCultures,
  search, setSearch,
  selectedId, onSelect,
  onImport, onDownloadTemplate,
  onExportPDF, onExportExcel,
  onDeleteParcel, onClearAll,
  onStartDraw,
  onParcelSheet, onOpenOwnerSheet,
  onOpenHistory,
  importing,
}) {
  const fileRef = useRef(null);

  const culturesCount = useMemo(() => parcels.filter((p) => p.source === "xml").length, [parcels]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parcels;
    return parcels.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.owner || "").toLowerCase().includes(q) ||
        (p.crop || "").toLowerCase().includes(q) ||
        (p.numero || "").toLowerCase().includes(q)
    );
  }, [parcels, search]);

  const owners = useMemo(() => {
    const set = new Map();
    parcels.forEach((p) => {
      const key = p.crop_label || p.crop || p.owner || p.name;
      if (!set.has(key)) set.set(key, colorForKey(key));
    });
    return Array.from(set.entries());
  }, [parcels]);

  return (
    <aside className="flex h-full w-full flex-col overflow-y-auto bg-[#f7f6f1]" data-testid="sidebar">
      <div className="p-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <Layers size={14} /> Couches
        </div>
        <div className="space-y-2">
          <LayerToggle icon={MapIcon} label="Vue aérienne" checked={showAerial} onCheckedChange={setShowAerial} testid="toggle-aerial" />
          <LayerToggle icon={Layers} label="Cadastre" checked={showCadastre} onCheckedChange={setShowCadastre} testid="toggle-cadastre" />
          <LayerToggle icon={Sprout} label="Mes parcelles" checked={showParcels} onCheckedChange={setShowParcels} testid="toggle-parcels" count={parcels.filter((p) => p.source !== "xml").length} />
          <LayerToggle icon={Wheat} label="Cultures (Télépac)" checked={showCultures} onCheckedChange={setShowCultures} testid="toggle-cultures" count={culturesCount} />
        </div>
      </div>

      <Separator />

      <div className="space-y-2 p-4">
        <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500">
          <Upload size={14} /> Données
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.xml"
          className="hidden"
          data-testid="file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onImport(f);
            e.target.value = "";
          }}
        />
        <Button
          className="w-full bg-[#1a4731] text-white hover:bg-[#143726]"
          onClick={() => fileRef.current?.click()}
          disabled={importing}
          data-testid="import-btn"
        >
          <Upload size={16} className="mr-2" />
          {importing ? "Import en cours…" : "Importer (.xlsx / .xml)"}
        </Button>
        <Button variant="outline" className="w-full border-stone-300" onClick={onDownloadTemplate} data-testid="template-btn">
          <Download size={16} className="mr-2" /> Modèle Excel
        </Button>
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" className="border-[#e8c872] text-[#8a6d1f] hover:bg-[#fdf6e3]" onClick={onExportPDF} disabled={!parcels.length} data-testid="export-pdf-btn">
            <FileDown size={16} className="mr-1" /> PDF
          </Button>
          <Button variant="outline" className="border-[#e8c872] text-[#8a6d1f] hover:bg-[#fdf6e3]" onClick={onExportExcel} disabled={!parcels.length} data-testid="export-excel-btn">
            <FileSpreadsheet size={16} className="mr-1" /> Excel
          </Button>
        </div>
        <Button variant="outline" className="w-full border-[#e8c872] text-[#8a6d1f] hover:bg-[#fdf6e3]" onClick={onOpenOwnerSheet} disabled={!parcels.some((p) => p.source !== "xml" && p.owner)} data-testid="owner-sheet-btn">
          <UserRound size={16} className="mr-2" /> Fiche par propriétaire
        </Button>
        {parcels.length > 0 && (
          <Button variant="ghost" className="w-full text-red-600 hover:bg-red-50" onClick={onClearAll} data-testid="clear-all-btn">
            <Eraser size={16} className="mr-2" /> Tout effacer
          </Button>
        )}
      </div>

      <Separator />

      <div className="flex min-h-0 flex-1 flex-col p-4" style={{ minHeight: "260px" }}>
        <div className="relative mb-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Rechercher une parcelle…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-input"
          />
        </div>

        <ScrollArea className="min-h-[120px] flex-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-10 text-center text-sm text-stone-400" data-testid="empty-list">
              {parcels.length === 0 ? "Aucune parcelle. Importez un fichier pour commencer." : "Aucun résultat."}
            </div>
          ) : (
            <div className="space-y-1.5 pr-2">
              {filtered.map((p) => {
                const color = colorForKey(p.crop_label || p.crop || p.owner || p.name);
                const active = p.id === selectedId;
                return (
                  <div
                    key={p.id}
                    onClick={() => onSelect(p.id)}
                    data-testid={`parcel-item-${p.id}`}
                    className={`group flex w-full cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors ${active ? "border-[#1a4731] bg-[#eef4ef]" : "border-stone-200 bg-white hover:bg-stone-50"}`}
                  >
                    <span className="h-8 w-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-stone-800">{p.name}</div>
                      <div className="truncate text-xs text-stone-500">
                        {[p.owner, p.crop_label || p.crop].filter(Boolean).join(" · ") || (p.polygon ? "Localisée" : "Sans contour")}
                      </div>
                    </div>
                    {p.area != null && <span className="shrink-0 text-xs font-medium text-stone-600">{p.area} ha</span>}
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenHistory(p); }}
                      className="shrink-0 text-stone-300 transition-colors hover:text-[#1a4731]"
                      title="Historique des cultures"
                      data-testid={`history-parcel-${p.id}`}
                    >
                      <History size={15} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onParcelSheet(p); }}
                      className="shrink-0 text-stone-300 transition-colors hover:text-[#8a6d1f]"
                      title="Fiche PDF"
                      data-testid={`sheet-parcel-${p.id}`}
                    >
                      <FileText size={15} />
                    </button>
                    {!p.polygon && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onStartDraw(p); }}
                        className="shrink-0 text-stone-300 transition-colors hover:text-[#1a4731]"
                        title="Tracer le contour"
                        data-testid={`draw-parcel-${p.id}`}
                      >
                        <Pencil size={15} />
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteParcel(p.id); }}
                      className="shrink-0 text-stone-300 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"
                      title="Supprimer"
                      data-testid={`delete-parcel-${p.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {owners.length > 0 && (
          <div className="mt-3 border-t border-stone-200 pt-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-500">Légende</div>
            <div className="flex flex-wrap gap-2">
              {owners.slice(0, 8).map(([name, color]) => (
                <span key={name} className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-white px-2 py-1 text-xs text-stone-600">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                  {name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
